// League Data Preloader Service
// Preloads and caches data for all user leagues on app startup
// Enables instant league switching with cached data

import { getDatabase, ref, get } from "firebase/database";
import { firestore } from './firebase';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { cacheService } from './cacheService';

class LeagueDataPreloader {
  constructor() {
    this.isPreloading = false;
    this.preloadedLeagues = new Set();
    this.preloadPromises = new Map();
  }

  /**
   * Preload all data for user's leagues on app startup
   */
  async preloadAllLeagueData(user, userLeagues, currentSeason) {
    if (this.isPreloading || !user || !userLeagues?.length) {
      return;
    }

    this.isPreloading = true;
    console.log(`🚀 Preloading data for ${userLeagues.length} leagues...`);

    try {
      // Preload all leagues in parallel
      const preloadPromises = userLeagues.map(league => 
        this.preloadSingleLeague(user, league, currentSeason)
      );

      await Promise.all(preloadPromises);
      console.log(`✅ Successfully preloaded data for ${userLeagues.length} leagues`);
    } catch (error) {
      console.warn('⚠️ Some league data failed to preload:', error);
    } finally {
      this.isPreloading = false;
    }
  }

  /**
   * Preload all data for a single league
   */
  async preloadSingleLeague(user, league, currentSeason) {
    if (this.preloadedLeagues.has(league.id)) {
      return this.preloadPromises.get(league.id);
    }

    const preloadPromise = this._doPreloadSingleLeague(user, league, currentSeason);
    this.preloadPromises.set(league.id, preloadPromise);

    try {
      await preloadPromise;
      this.preloadedLeagues.add(league.id);
      console.log(`✅ Preloaded data for league: ${league.name}`);
    } catch (error) {
      console.warn(`⚠️ Failed to preload data for league ${league.name}:`, error);
      this.preloadPromises.delete(league.id);
    }

    return preloadPromise;
  }

  async _doPreloadSingleLeague(user, league, currentSeason) {
    // Parallel data loading
    const dataLoaders = [
      this._preloadLeagueInfo(league),
      this._preloadLeagueMembers(league.id),
      this._preloadUserPredictions(user, league.id, currentSeason),
      this._preloadUserTitles(user, league.id),
      this._preloadLeagueStandings(league.id, currentSeason),
    ];

    await Promise.all(dataLoaders);
  }

  /**
   * Cache enhanced league info
   */
  async _preloadLeagueInfo(league) {
    const cacheKey = cacheService.getCacheKey('leagueInfo', league.id);
    if (cacheService.has(cacheKey)) return;

    // Refresh league info from Firestore
    try {
      const leagueDocRef = doc(firestore, 'leagues', league.id);
      const leagueDoc = await getDoc(leagueDocRef);
      
      if (leagueDoc.exists()) {
        const freshLeagueData = { id: leagueDoc.id, ...leagueDoc.data() };
        cacheService.setLeagueInfo(league.id, freshLeagueData);
      }
    } catch (error) {
      // Fallback to provided league data
      cacheService.setLeagueInfo(league.id, league);
    }
  }

  /**
   * Cache league members
   */
  async _preloadLeagueMembers(leagueId) {
    const cacheKey = cacheService.getCacheKey('leagueMembers', leagueId);
    if (cacheService.has(cacheKey)) return;

    try {
      const membersQuery = query(
        collection(firestore, 'leagues', leagueId, 'members'),
        where('status', '==', 'active')
      );
      const membersSnapshot = await getDocs(membersQuery);
      const members = membersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      cacheService.set(cacheKey, members, 2 * 60 * 60 * 1000); // 2 hours
    } catch (error) {
      console.warn(`Failed to preload members for league ${leagueId}:`, error);
    }
  }

  /**
   * Cache user predictions for the league
   */
  async _preloadUserPredictions(user, leagueId, currentSeason) {
    const cacheKey = cacheService.getCacheKey('userPredictions', `${user.uid}_${leagueId}_${currentSeason}`);
    if (cacheService.has(cacheKey)) return;

    try {
      const db = getDatabase();
      const userPredictionsRef = ref(db, `leagues/${leagueId}/predictions/${currentSeason}/${user.displayName}`);
      const snapshot = await get(userPredictionsRef);
      
      if (snapshot.exists()) {
        cacheService.set(cacheKey, snapshot.val(), 30 * 60 * 1000); // 30 minutes
      }
    } catch (error) {
      console.warn(`Failed to preload predictions for user in league ${leagueId}:`, error);
    }
  }

  /**
   * Cache user titles for the league
   */
  async _preloadUserTitles(user, leagueId) {
    const cacheKey = cacheService.getCacheKey('userTitles', `${user.uid}_${leagueId}`);
    if (cacheService.has(cacheKey)) return;

    try {
      const memberDocRef = doc(firestore, 'leagues', leagueId, 'members', user.uid);
      const memberDoc = await getDoc(memberDocRef);

      if (memberDoc.exists()) {
        const titles = memberDoc.data().titles || [];
        cacheService.set(cacheKey, titles, 6 * 60 * 60 * 1000); // 6 hours
      }
    } catch (error) {
      console.warn(`Failed to preload titles for user in league ${leagueId}:`, error);
    }
  }

  /**
   * Cache league standings/scores for the current season
   */
  async _preloadLeagueStandings(leagueId, currentSeason) {
    const cacheKey = cacheService.getCacheKey('leagueStandings', `${leagueId}_${currentSeason}`);
    if (cacheService.has(cacheKey)) return;

    try {
      const db = getDatabase();
      const predictionsRef = ref(db, `leagues/${leagueId}/predictions/${currentSeason}`);
      const snapshot = await get(predictionsRef);
      
      if (snapshot.exists()) {
        const scoresData = snapshot.val();
        const standings = Object.keys(scoresData).map((username) => ({
          username,
          totalScore: scoresData[username].totalScore || 0,
        })).sort((a, b) => b.totalScore - a.totalScore);
        
        cacheService.set(cacheKey, standings, 30 * 60 * 1000); // 30 minutes
      }
    } catch (error) {
      console.warn(`Failed to preload standings for league ${leagueId}:`, error);
    }
  }

  /**
   * Get cached data for instant league switching
   */
  getCachedLeagueData(leagueId, dataType, userId = null, season = null) {
    let cacheKey;
    
    switch (dataType) {
      case 'leagueInfo':
        cacheKey = cacheService.getCacheKey('leagueInfo', leagueId);
        break;
      case 'leagueMembers':
        cacheKey = cacheService.getCacheKey('leagueMembers', leagueId);
        break;
      case 'userPredictions':
        cacheKey = cacheService.getCacheKey('userPredictions', `${userId}_${leagueId}_${season}`);
        break;
      case 'userTitles':
        cacheKey = cacheService.getCacheKey('userTitles', `${userId}_${leagueId}`);
        break;
      case 'leagueStandings':
        cacheKey = cacheService.getCacheKey('leagueStandings', `${leagueId}_${season}`);
        break;
      default:
        return null;
    }
    
    return cacheService.get(cacheKey);
  }

  /**
   * Check if league data is fully cached
   */
  isLeagueDataCached(leagueId) {
    return this.preloadedLeagues.has(leagueId);
  }

  /**
   * Background refresh of cached data
   */
  async backgroundRefreshLeague(user, league, currentSeason) {
    // Don't block user interactions, run in background
    setTimeout(async () => {
      try {
        await this._doPreloadSingleLeague(user, league, currentSeason);
        console.log(`🔄 Background refresh completed for league: ${league.name}`);
      } catch (error) {
        console.warn(`Background refresh failed for league ${league.name}:`, error);
      }
    }, 1000); // Delay to not interfere with user interactions
  }

  /**
   * Clear preloaded data (useful for logout or cache invalidation)
   */
  clearPreloadedData() {
    this.preloadedLeagues.clear();
    this.preloadPromises.clear();
    this.isPreloading = false;
  }
}

// Export singleton instance
export const leagueDataPreloader = new LeagueDataPreloader();
export default LeagueDataPreloader;